import PropTypes from 'prop-types'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {getDefaultSite} from '@salesforce/retail-react-app/app/utils/site-utils'
import fetch from 'cross-fetch'

interface L10nConfig {
    supportedLocales: {id: string}[]
    defaultLocale: string
}

interface GetTargetLocaleOptions {
    getUserPreferredLocales?: () => string[]
    l10nConfig?: L10nConfig
}

/**
 * Dynamically import the translations/messages for a given locale
 * @param {string} locale
 * @param {string} origin
 * @returns {Promise<Record<string, unknown>>} The messages (compiled in AST format) in the given locale.
 *      If the translation file is not found, return an empty object (so react-intl would fall back to the inline messages)
 */
export const fetchTranslations = async (locale, origin) => {
    const targetLocale = typeof window === 'undefined' ? (process.env.USE_PSEUDOLOCALE === 'true' ? 'en-XA' : locale) : locale

    try {
        const file = `${origin || getAppOrigin()}${getAssetUrl(`static/translations/compiled/${targetLocale}.json`)}`
        const response = await fetch(file)

        if (!response.ok) {
            throw new Error(`Failed to fetch ${file}. Received the response: ${response.status} ${response.statusText}`)
        }

        return await response.json()
    } catch (err) {
        logger.error('Translation not found. Loading empty messages, so that react-intl would fall back to the inline default messages', {
            namespace: 'utils.fetchTranslations',
            additionalProperties: {error: err},
        })
        return {}
    }
}

/**
 * Determine the target locale by comparing the users' preferred locales with the app's supported locales
 * @param {GetTargetLocaleOptions} options
 * @returns {string} The target locale's short code
 */
export const getTargetLocale = ({getUserPreferredLocales, l10nConfig = {supportedLocales: [], defaultLocale: ''}}: GetTargetLocaleOptions = {}): string => {
    const userPreferredLocales = getUserPreferredLocales ? getUserPreferredLocales() : []
    const supportedLocales = l10nConfig.supportedLocales.map((locale) => locale.id)
    const defaultLocale = l10nConfig.defaultLocale

    const targetLocale = determineTargetLocale(userPreferredLocales, supportedLocales, defaultLocale)
    return targetLocale
}

/**
 * Map locale codes for Intl APIs only.
 * Keep app locale untouched (e.g. ch-HK), but use a BCP47 compatible language for Intl.
 * See the list of languages supported here: https://gist.github.com/raushankrjha/d1c7e35cf87e69aa8b4208a8171a8416
 */
export const normalizeLocaleForIntl = (locale: string): string => {
    if (!locale) return locale

    const [language, region, ...rest] = locale.split('-')
    if (!language) return locale

    if (language.toLowerCase() !== 'ch') {
        return locale
    }

    const normalizedRegion = region?.toUpperCase()
    if (normalizedRegion !== 'HK' && normalizedRegion !== 'MO' && normalizedRegion !== 'TW') {
        return locale
    }

    return ['zh', region, ...rest].join('-')
}

/**
 * Decide which locale to load
 * @private
 * @param {string[]} preferredLocales - All locales that the user prefers
 * @param {string[]} supportedLocales - All locales that your app supports
 * @param {string} fallbackLocale - App's default locale
 * @returns {string} The target locale if there's a match. Otherwise, returns `fallbackLocale`.
 */
export const determineTargetLocale = (preferredLocales: string[], supportedLocales: string[], fallbackLocale: string): string => {
    const targetLocale = preferredLocales.find((locale) => supportedLocales.includes(locale))
    return targetLocale || fallbackLocale
}

export const MESSAGE_PROPTYPE = PropTypes.shape({
    // NOTE: defaultMessage is typically written as a string
    // but its value can be an array when it comes from the compiled AST version
    defaultMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
    id: PropTypes.string,
})

/**
 *
 * @description
 * The findSiteAndLocaleByAlias function searches through a list of sites to find a site and locale that matches a given alias. If no matching locale is found, it returns the default site and locale.
 *
 * @param {Object[]} sites - Properties object
 * @param {string} alias - Title for the locale modal.
 * @return {{Object, Object}}
 */
export const findSiteAndLocaleByAlias = (sites, alias) => {
    if (alias && typeof alias === 'string') {
        alias = alias.replace('ch-', 'zh-')
    }

    for (const site of sites) {
        const matchingLocale = site.l10n.supportedLocales.find((locale) => locale.alias === alias)
        if (matchingLocale) {
            return {
                site: site,
                locale: matchingLocale,
            }
        }
    }
    const defaultSite = getDefaultSite()
    const defaultLocale = defaultSite.l10n.supportedLocales.find((locale) => locale.alias === defaultSite.defaultLocale)
    return {
        site: defaultSite,
        locale: defaultLocale,
    }
}
